/**
 * SEO Cross-Tool Synthetic Metrics (Story 20.9 — Epic 20 cherry-on-top)
 *
 * Why this module exists:
 *   No competitor reporting tool surfaces a single 3-tool authority
 *   comparison (DR vs AS vs DA) because joining 3 different time-series
 *   surfaces requires the Moz V3 has-no-time-series-API workaround:
 *   Ahrefs has `/domain-rating-history`, Semrush has `domain_rank_history`,
 *   Moz has NO time-series API. ProvenLeap built the Moz snapshot
 *   persistence layer in Stories 20.7+20.8; this story ships the parallel
 *   Ahrefs DR + Semrush AS snapshot layers that close the cross-tool
 *   join — the category-defining build-to-sell payoff per
 *   `_bmad/bmm/data/build-to-sell-directive.md`.
 *
 * Architectural shape:
 *   Both metrics are SYNTHETIC AGGREGATES — they do NOT correspond to
 *   wire-format API fields on any single SEO tool. They are computed at
 *   preset-expansion time from a join of the 3 per-tool baselines
 *   (Scoreboard) or the 3 `*_authority_snapshots` D1 tables (Trend).
 *   `apiField` is stamped `_synthetic_cross_tool_aggregate` so the
 *   metric-allowlist build-time test exempts them from the per-category
 *   wire-field allowlist check via `SEO_CROSS_TOOL_SYNTHETIC_METRIC_KEYS`.
 *
 * @see `_bmad-output/implementation-artifacts/20-9-seo-unified-metrics-widget.md` AC #2
 */

import type { DimensionDefinition, MetricDefinition } from "../adapter";

/**
 * Sentinel `apiField` for the cross-tool synthetic aggregates.  The
 * `metric-allowlist.test.ts` build-time test exempts metrics with this
 * apiField from the wire-field-must-match-API-field invariant per Story
 * 20.6 / 20.8 convention.
 */
export const SEO_CROSS_TOOL_SYNTHETIC_API_FIELD =
  "_synthetic_cross_tool_aggregate";

/**
 * 2 synthetic-aggregate metric definitions covering the cross-SEO surface.
 * Story 20.9 ships ONLY these 2 — every per-tool wire metric remains in its
 * own `MOZ_METRICS` / `AHREFS_METRICS` / `SEMRUSH_METRICS` array unchanged.
 */
export const SEO_CROSS_TOOL_METRICS: MetricDefinition[] = [
  {
    key: "seoAuthorityScoreboard",
    label: "SEO Authority Scoreboard (3-Tool)",
    type: "number",
    category: "seo-cross-tool",
    dataType: "Decimal",
    // Story 31-31 — 0-100 authority scores have no volume weight (unweighted-mean fallback).
    aggregationKind: "ratio",
    compatibleWidgets: ["kpi", "bar-chart"],
    apiField: SEO_CROSS_TOOL_SYNTHETIC_API_FIELD,
    description:
      "Synthetic — 3-element array { platform: 'ahrefs'|'semrush'|'moz', score: 0-100, label: string } computed at widget-expand-time from Ahrefs domainRating + Semrush authorityScore + Moz domainAuthority baselines. Industry-standard 3-tool cross-comparison answer to 'what's our SEO authority?' Built-in calibration footnote explains scale divergence.",
  },
  {
    key: "seoAuthorityTrend3Tool",
    label: "SEO Authority 6-Month Trend (3-Tool)",
    type: "number",
    category: "seo-cross-tool",
    dataType: "Decimal",
    // Story 31-31 — authority-score time series; scores have no volume weight (unweighted-mean fallback).
    aggregationKind: "ratio",
    compatibleWidgets: ["line-chart"],
    apiField: SEO_CROSS_TOOL_SYNTHETIC_API_FIELD,
    description:
      "Synthetic — 6-element array { capturedAt: 'YYYY-MM-15', ahrefsDR: 0-100|null, semrushAS: 0-100|null, mozDA: 0-100|null } computed at widget-expand-time from ahrefs_authority_snapshots + semrush_authority_snapshots + moz_authority_snapshots D1 tables, joined on monthly bucket. Build-to-sell payoff — no competitor reporting tool surfaces this because joining 3 different time-series surfaces requires the Moz V3 has-no-time-series-API workaround (ProvenLeap snapshot persistence).",
  },
];

/**
 * Set of synthetic-aggregate metric keys whose `apiField` is the
 * `_synthetic_cross_tool_aggregate` sentinel.  Mirrors Story 20.8's
 * `MOZ_SYNTHETIC_METRIC_KEYS` runtime-immutable Proxy pattern (Pass 2
 * BH2-1 hardening + Pass 3 BH3-5 forEach-leak fix).
 *
 * **DO NOT add a metric here to "fix" a test failure.** This Set is
 * reserved for metrics that are genuinely computed at preset-expand time
 * from D1 snapshot data or aggregate reducers, not from wire-format API
 * fields.
 */
const SEO_CROSS_TOOL_SYNTHETIC_METRIC_KEYS_INTERNAL = new Set<string>([
  "seoAuthorityScoreboard",
  "seoAuthorityTrend3Tool",
]);

const SET_MUTATION_METHODS = new Set<string>(
  ["add", "delete", "clear"] as const,
);

/**
 * Read-only Set proxy. Forwards reads to the underlying Set but throws on
 * `add`/`delete`/`clear` calls. Mirrors Moz Story 20.8 `asReadonlySet` —
 * see [`packages/integrations/src/moz/moz.ts:595`] for the original.
 *
 * - `Set.prototype.size` is a getter, so the trap forwards reads against
 *   the underlying Set (not the proxy receiver) — otherwise V8 throws
 *   "Method get Set.prototype.size called on incompatible receiver".
 * - `forEach` is special-cased to substitute the proxy as the 3rd callback
 *   argument so consumers cannot escape the readonly contract through the
 *   `set` parameter (Pass 3 BH3-5 leak defense).
 */
function asReadonlySet<T>(source: Set<T>): ReadonlySet<T> {
  // `proxyRef` must be `let` because the Proxy closure below captures the
  // reference BEFORE the assignment at the end of this function; mirrors
  // Moz Story 20.8 `asReadonlySet` pattern.
  // eslint-disable-next-line prefer-const
  let proxyRef: ReadonlySet<T>;
  const proxy = new Proxy(source, {
    get(target, prop, _receiver) {
      if (typeof prop === "string" && SET_MUTATION_METHODS.has(prop)) {
        return () => {
          throw new TypeError(
            `Cannot mutate SEO_CROSS_TOOL_SYNTHETIC_METRIC_KEYS — Set is read-only at runtime per build-to-sell-directive. Use SEO_CROSS_TOOL_METRICS expansions instead.`,
          );
        };
      }
      if (prop === "forEach") {
        return (
          callback: (value: T, value2: T, set: ReadonlySet<T>) => void,
          thisArg?: unknown,
        ): void => {
          target.forEach((v) => {
            callback.call(thisArg, v, v, proxyRef);
          });
        };
      }
      const value = Reflect.get(target, prop, target);
      return typeof value === "function" ? value.bind(target) : value;
    },
  }) as ReadonlySet<T>;
  proxyRef = proxy;
  return proxy;
}

export const SEO_CROSS_TOOL_SYNTHETIC_METRIC_KEYS: ReadonlySet<string> =
  asReadonlySet(SEO_CROSS_TOOL_SYNTHETIC_METRIC_KEYS_INTERNAL);

/**
 * Single synthetic dimension materializing the bar-chart x-axis used by the
 * Scoreboard preset. Not registered with any adapter (this module has no
 * `getAvailableDimensions()` because it has no `ADAPTERS` entry) — the
 * export exists to keep the catalog row (`_bmad/bmm/data/integration-metrics/
 * seo-cross-tool.md`) honest: the dimension is real (it drives the
 * bar-chart x-axis tick labels) and code now mirrors that contract.
 *
 * The `Tool` semantic in the catalog maps to `Classification` in the
 * `DimensionDefinition` union — `Classification` is the closest fit since
 * the values are tool-identity labels, not a Time/Geo/Audience axis.
 */
export const SEO_CROSS_TOOL_DIMENSIONS: DimensionDefinition[] = [
  {
    key: "platform",
    label: "Tool / Platform",
    type: "Classification",
    exampleValues: ["ahrefs", "semrush", "moz"],
  },
];
